import { useReducer } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { enableContextualFeedbackBot } from '@gonfalon/dogfood-flags/dogfood-flags';
import { isReader as isReaderProfile } from '@gonfalon/members/isReader';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button, Checkbox, IconButton, Text, TextArea, TextField, ToggleIconButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { FeedbackSubmissionError, sendFeedbackToDestinations } from './sendFeedbackToDestinations';

import styles from './ContextualFeedbackActive.module.css';

const FEEDBACK_MAX_LENGTH = 3500;

type Step = 'sentiment' | 'form' | 'success' | 'gone';
type Sentiment = 'positive' | 'negative';

interface WidgetState {
  step: Step;
  sentiment: Sentiment | null;
  shareEmail: boolean;
}

type WidgetAction =
  | { type: 'SELECT_SENTIMENT'; sentiment: Sentiment }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'DISMISS' }
  | { type: 'GONE' }
  | { type: 'SET_SHARE_EMAIL'; value: boolean };

function widgetReducer(state: WidgetState, action: WidgetAction): WidgetState {
  switch (action.type) {
    case 'SELECT_SENTIMENT':
      return { ...state, step: 'form', sentiment: action.sentiment };
    case 'SUBMIT_SUCCESS':
      return { ...state, step: 'success' };
    case 'DISMISS':
    case 'GONE':
      return { ...state, step: 'gone' };
    case 'SET_SHARE_EMAIL':
      return { ...state, shareEmail: action.value };
    default:
      return state;
  }
}

interface FeedbackFormData {
  feedback: string;
}

export interface ContextualFeedbackActiveProps {
  /**
   * Recipient flag key. Feedback is submitted to the qualitative feedback product via the SDK and tied to this flag.
   * Also scopes the per-session dismissed state so multiple widgets on different surfaces are independent.
   */
  flagKey: string;
  /**
   * When true, the widget becomes eligible to appear.
   * Pass true once the user has meaningfully progressed through the flow.
   */
  isComplete: boolean;
  /** The sentiment question shown in the first step. */
  prompt: string;
}

/**
 * Floating feedback widget for step-wise product flows.
 * Appears bottom-right once `isComplete` is true, prompting the user with
 * thumbs up/down, then optionally a free-form text input.
 *
 * Only shown once per browser session (uses sessionStorage).
 */
export function ContextualFeedbackActive({ flagKey, isComplete, prompt }: ContextualFeedbackActiveProps) {
  const { profile } = useAccountContext();
  const isReader = isReaderProfile(profile);

  const [dismissed, dismissedSlot] = useStorageSlot(`ld-contextual-feedback-${flagKey}-dismissed`, {
    storageType: 'sessionStorage',
  });

  const [state, dispatch] = useReducer(widgetReducer, {
    step: dismissed ? 'gone' : 'sentiment',
    sentiment: null,
    shareEmail: true,
  });
  const { step, sentiment, shareEmail } = state;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<FeedbackFormData>({ mode: 'onSubmit' });

  if (
    !isComplete ||
    isReader ||
    !enableContextualFeedbackBot() ||
    step === 'gone' ||
    (dismissed && step !== 'success')
  ) {
    return null;
  }

  // Record whether the user consented to follow-up. Their identity is recoverable from the
  // feedback's session, so we store the consent flag rather than their email address.
  const customProperties = { shareEmail };

  const dismiss = () => {
    if (sentiment !== null && step === 'form') {
      // User selected sentiment but abandoned the text form — capture it fire-and-forget
      sendFeedbackToDestinations({
        flagKey,
        feedback: '',
        sentiment,
        customProperties,
      }).catch(() => {});
    }
    dismissedSlot.set('1');
    dispatch({ type: 'DISMISS' });
  };

  const handleSentimentSelect = (selected: Sentiment) => {
    dispatch({ type: 'SELECT_SENTIMENT', sentiment: selected });
  };

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      await sendFeedbackToDestinations({
        flagKey,
        feedback: data.feedback,
        sentiment,
        customProperties,
      });
      reset();
      dismissedSlot.set('1');
      dispatch({ type: 'SUBMIT_SUCCESS' });
      setTimeout(() => {
        dispatch({ type: 'GONE' });
      }, 3000);
    } catch (err) {
      setError('root', {
        message:
          err instanceof FeedbackSubmissionError
            ? err.message
            : 'An error occurred while sending feedback. Please try again.',
      });
    }
  };

  const submitFeedback = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    await handleSubmit(onSubmit)();
  };

  // Render via portal to document.body so the <form> is never nested inside
  // the parent <Form> from DraftFormTemplate. Nested <form> elements are
  // invalid HTML and cause the submit to target the outer form, triggering a
  // page refresh and the NavigationPrompt "Leave site?" dialog.
  return createPortal(
    <div className={styles.widget} role="complementary" aria-label={msg('feedback.contextualWidget.widgetLabel')}>
      <div className={styles.card}>
        {step !== 'success' && (
          <IconButton
            aria-label={msg('feedback.contextualWidget.dismissLabel')}
            icon="cancel"
            variant="minimal"
            size="small"
            className={styles.dismissButton}
            onPress={dismiss}
            isDisabled={isSubmitting}
          />
        )}

        {step === 'sentiment' && (
          <>
            <Text elementType="p" className={styles.prompt}>
              {prompt}
            </Text>
            <div className={styles.sentimentRow} role="group" aria-label={msg('feedback.contextualWidget.ratingLabel')}>
              <ToggleIconButton
                icon="thumb-up-outline"
                aria-label={msg('feedback.contextualWidget.thumbsUpLabel')}
                isSelected={sentiment === 'positive'}
                onPress={() => handleSentimentSelect('positive')}
              />
              <ToggleIconButton
                icon="thumb-down-outline"
                aria-label={msg('feedback.contextualWidget.thumbsDownLabel')}
                isSelected={sentiment === 'negative'}
                onPress={() => handleSentimentSelect('negative')}
              />
            </div>
          </>
        )}

        {step === 'form' && (
          <form onSubmit={(e) => submitFeedback(e)} className={styles.form}>
            <div className={styles.formHeader}>
              <div className={styles.selectedSentiment}>
                <Icon name={sentiment === 'positive' ? 'thumb-up-outline' : 'thumb-down-outline'} />
              </div>
              <Text elementType="p" className={styles.formPrompt}>
                <Message id="feedback.contextualWidget.commentPrompt" />{' '}
                <Text elementType="span" className={styles.optional}>
                  (optional)
                </Text>
              </Text>
            </div>

            <TextField aria-label={msg('feedback.contextualWidget.commentLabel')} className={styles.textField}>
              <TextArea
                autoFocus
                {...register('feedback', {
                  setValueAs: (value: string) => value.trim(),
                  maxLength: {
                    value: FEEDBACK_MAX_LENGTH,
                    message: `Feedback is too long. Max ${FEEDBACK_MAX_LENGTH} characters.`,
                  },
                })}
                placeholder={msg('feedback.contextualWidget.commentPlaceholder')}
                className={styles.textArea}
              />
              {errors.feedback && <span className={styles.error}>{errors.feedback.message}</span>}
              {errors.root && <span className={styles.error}>{errors.root.message}</span>}
            </TextField>

            <Checkbox
              isSelected={shareEmail}
              onChange={(v) => dispatch({ type: 'SET_SHARE_EMAIL', value: v })}
              className={styles.checkbox}
            >
              <Message id="feedback.contextualWidget.shareEmail" />
            </Checkbox>

            <div className={styles.formFooter}>
              <Button variant="primary" type="submit" isDisabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Submit'}
              </Button>
            </div>
          </form>
        )}

        {step === 'success' && (
          <div className={styles.success}>
            <span className={styles.successEmoji}>🙏</span>
            <Text elementType="p" className={styles.successText}>
              <Message id="feedback.contextualWidget.thanks" />
            </Text>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
